import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { fetchAuthSession } from '@aws-amplify/auth';
import './JourneyEditModal.css';

const apiUrl = process.env.REACT_APP_API_URL;

const JourneyEditModal = ({ journey, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    journeyColor: { r: 0, g: 0, b: 0 },
    displayItemsMode: 'Both',
    manualPercentComplete: 0
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (journey) {
      setFormData({
        journeyColor: journey.journeyColor || { r: 0, g: 0, b: 0 },
        displayItemsMode: journey.displayItemsMode || 'Both',
        manualPercentComplete: journey.manualPercentComplete || 0
      });
    }
  }, [journey]);

  const getUserFromToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub;
    } catch (error) {
      console.error('Error parsing JWT token:', error);
      return null;
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const user = getUserFromToken(token);

      if (!user) {
        throw new Error('Failed to retrieve user from JWT token.');
      }

      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };

      const url = `${apiUrl}/users/${encodeURIComponent(user)}`;
      
      // Note: JourneyEditModal doesn't have access to all journeys (only receives single journey prop)
      // So we still need GET here to get the full journeys array
      // This could be optimized by passing journeys array as a prop, but GET is acceptable here
      // since editing is less frequent than quick actions (favorite/complete buttons)
      const getUserResponse = await axios.get(url, { headers });
      const currentUserData = Array.isArray(getUserResponse.data) ? getUserResponse.data[0] : getUserResponse.data;
      const existingJourneys = currentUserData?.journeys || [];

      // Find and update the journey
      const journeyIndex = existingJourneys.findIndex(
        j => j.elementID === journey.journeyID
      );

      if (journeyIndex < 0) {
        throw new Error('Journey not found in user journeys');
      }

      // Create updated journey object
      // Preserve existing overrides from the journey
      const updatedJourney = {
        ...existingJourneys[journeyIndex],
        journeyColor: formData.journeyColor,
        displayItemsMode: formData.displayItemsMode,
        manualPercentComplete: parseFloat(formData.manualPercentComplete)
        // overrides are preserved from existingJourneys[journeyIndex]
      };

      // Update journeys array
      const updatedJourneys = [...existingJourneys];
      updatedJourneys[journeyIndex] = updatedJourney;

      // Send PATCH request
      const patchData = {
        journeys: updatedJourneys
      };

      await axios.patch(url, patchData, { headers });

      // Call onSave callback with updated journey data
      // Pass only the updated properties, not the full journey object
      if (onSave) {
        await onSave({
          journeyID: journey.journeyID,
          journeyColor: formData.journeyColor,
          displayItemsMode: formData.displayItemsMode,
          manualPercentComplete: parseFloat(formData.manualPercentComplete)
          // overrides are preserved from existing journey data
        });
      }

      onClose();
    } catch (error) {
      console.error('Error updating journey:', error);
      alert('Failed to update journey. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleColorChange = (r, g, b) => {
    setFormData(prev => ({
      ...prev,
      journeyColor: { r, g, b }
    }));
  };

  // Color palette matching the provided image: 44 colors in 4 rows x 11 columns
  const presetColors = [
    // Row 1 - Reds & Oranges (High visibility warm tones - stand out excellently on maps)
    { r: 255, g: 99, b: 71 },   // Coral red - vibrant and visible
    { r: 220, g: 20, b: 60 },   // Crimson - bold and striking
    { r: 255, g: 0, b: 0 },     // Pure red - classic high contrast
    { r: 178, g: 34, b: 34 },   // Firebrick - deep red
    { r: 255, g: 69, b: 0 },    // Red-orange - energetic
    { r: 255, g: 140, b: 0 },   // Dark orange - warm and visible
    { r: 255, g: 165, b: 0 },   // Orange - bright and cheerful
    { r: 255, g: 215, b: 0 },   // Gold - luxurious
    { r: 255, g: 255, b: 0 },   // Yellow - maximum visibility
    { r: 240, g: 230, b: 140 }, // Khaki - soft yellow
    { r: 255, g: 182, b: 193 }, // Light pink - soft and pleasant
    
    // Row 2 - Purples & Magentas (Excellent map contrast - rare in nature, very distinctive)
    { r: 255, g: 20, b: 147 },  // Deep pink - very distinctive
    { r: 255, g: 105, b: 180 }, // Hot pink - playful
    { r: 219, g: 112, b: 147 }, // Pale violet red - elegant
    { r: 186, g: 85, b: 211 },  // Medium orchid - vibrant purple
    { r: 138, g: 43, b: 226 },  // Blue violet - rich
    { r: 147, g: 112, b: 219 }, // Medium purple - balanced
    { r: 153, g: 50, b: 204 },  // Dark orchid - bold
    { r: 128, g: 0, b: 128 },   // Purple - classic
    { r: 75, g: 0, b: 130 },    // Indigo - deep
    { r: 106, g: 90, b: 205 },  // Slate blue - professional
    { r: 72, g: 61, b: 139 },   // Dark slate blue - sophisticated
    
    // Row 3 - Blues & Cyans (Contrast well with ocean blues - distinct and clear)
    { r: 0, g: 191, b: 255 },   // Deep sky blue - brilliant
    { r: 30, g: 144, b: 255 },  // Dodger blue - sporty
    { r: 65, g: 105, b: 225 },  // Royal blue - regal
    { r: 0, g: 0, b: 255 },     // Pure blue - maximum contrast
    { r: 25, g: 25, b: 112 },   // Midnight blue - dark and distinct
    { r: 0, g: 206, b: 209 },   // Dark turquoise - tropical
    { r: 64, g: 224, b: 208 },  // Turquoise - bright
    { r: 72, g: 209, b: 204 },  // Medium turquoise - balanced
    { r: 32, g: 178, b: 170 },  // Light sea green - aqua
    { r: 0, g: 139, b: 139 },   // Dark cyan - deep water
    { r: 95, g: 158, b: 160 },  // Cadet blue - muted
    
    // Row 4 - Greens & Earth Tones (Lime greens contrast with forest greens)
    { r: 50, g: 205, b: 50 },   // Lime green - high contrast with forests
    { r: 124, g: 252, b: 0 },   // Lawn green - vibrant
    { r: 127, g: 255, b: 0 },   // Chartreuse - electric
    { r: 173, g: 255, b: 47 },  // Green yellow - bright
    { r: 0, g: 255, b: 127 },   // Spring green - fresh
    { r: 60, g: 179, b: 113 },  // Medium sea green - balanced
    { r: 46, g: 139, b: 87 },   // Sea green - natural
    { r: 34, g: 139, b: 34 },   // Forest green - earthy
    { r: 107, g: 142, b: 35 },  // Olive drab - military
    { r: 160, g: 82, b: 45 },   // Sienna - warm brown
    { r: 139, g: 69, b: 19 }    // Saddle brown - rich earth
  ];

  if (!isOpen || !journey) return null;

  return (
    <div className="journey-edit-modal-overlay" onClick={onClose}>
      <div className="journey-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Journey Settings</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-content">
          <div className="journey-edit-title">{journey.title || 'Untitled Journey'}</div>

          <div className="form-section">
            <label className="form-label">Journey Color</label>
            <div className="color-palette">
              {presetColors.map((color, idx) => {
                const isSelected = formData.journeyColor.r === color.r && 
                                  formData.journeyColor.g === color.g && 
                                  formData.journeyColor.b === color.b;
                return (
                  <div
                    key={idx}
                    className={`color-swatch ${isSelected ? 'selected' : ''}`}
                    style={{ backgroundColor: `rgb(${color.r}, ${color.g}, ${color.b})` }}
                    onClick={() => handleColorChange(color.r, color.g, color.b)}
                    title={`RGB(${color.r}, ${color.g}, ${color.b})`}
                  >
                    {isSelected && <i className="fas fa-check"></i>}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="form-section">
            <label className="form-label">Display Mode</label>
            <select
              className="form-select"
              value={formData.displayItemsMode}
              onChange={(e) => setFormData(prev => ({ ...prev, displayItemsMode: e.target.value }))}
            >
              <option value="Both">Both Visited and Not Visited</option>
              <option value="visited">Visited Only</option>
              <option value="notVisited">Not Visited Only</option>
            </select>
          </div>

          <div className="form-section">
            <label className="form-label">
              Manual Completion Percentage: {formData.manualPercentComplete}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={formData.manualPercentComplete}
              onChange={(e) => setFormData(prev => ({ ...prev, manualPercentComplete: parseInt(e.target.value) }))}
              className="form-range"
            />
            <div className="range-labels">
              <span>0%</span>
              <span>100%</span>
            </div>
          </div>

        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JourneyEditModal;
